import useRuntimeConfig from '@vl-core/hooks/useConfig';
import { Alert, Button, Form, Input, Skeleton, Rule, Link, Divider } from 'antd';
import React from 'react';
import Align from '@components/UI/utilities/Align';
import LoginTitle from '@components/Login/LoginTitle';
import { validate } from 'nhs-number-validator';
import { DateTime } from 'luxon';
import { DateInput } from 'vl-common/src/components/DateInput';
import IdentityProviderButton from '@components/Login/IdentityProviderButton';
import useGuestAccessLogin from '@hooks/useGuestAccessLogin';
import { useTranslation } from 'react-i18next';
import ContactLinksHint from '@components/Login/ContactLinksHint';

const removeSpaces = (value: string) => value.replace(/\s/g, '');

const useGetNHSProvider = () => {
  const { IDENTITY_PROVIDERS } = useRuntimeConfig();

  return IDENTITY_PROVIDERS?.providers?.find((p) => p?.provider === 'NHSLogin');
};

const RAForm: React.FC<{
  isNHS?: boolean;
}> = ({ isNHS }) => {
  const NHSProvider = useGetNHSProvider();
  const { GUEST_ACCESS_RESEND } = useRuntimeConfig();
  const { stage, form, onFinish } = useGuestAccessLogin();
  const { t } = useTranslation();

  const dateOfBirthRules: Rule[] = [
    {
      validator(_, value) {
        try {
          const parsedDoB = DateTime.fromFormat(value, 'yyyy-M-d');

          if (!parsedDoB.isValid) {
            return Promise.reject(new Error(t('EnterValidDate')));
          }

          if (parsedDoB.diffNow().milliseconds > 0) {
            return Promise.reject(new Error(t('DOBMustBeInPast')));
          }

          if (parsedDoB.diffNow('years').years < -110) {
            return Promise.reject(new Error(t('CheckDOBCorrect')));
          }
        } catch (e) {
          console.log({ e });
          return Promise.reject(new Error(t('EnterValidDate')));
        }

        return Promise.resolve();
      }
    }
  ];

  if (stage === 'preparing') {
    return <Skeleton active />;
  }

  if (stage === 'invalid') {
    return (
      <Alert
        showIcon
        message={t('This link is invalid.')}
        type="info"
        description={
          <>
            <p>
              {GUEST_ACCESS_RESEND ? (
                <>
                  {t(
                    'If it has expired, you will receive a new link soon. If you have signed in with NHS Login before, this link will no longer work and you will need to login with NHS Login'
                  )}{' '}
                  <Link href="/login">{t('Here')}</Link>.
                </>
              ) : (
                <Link href="/login">{t('ReturnToLoginPage')}</Link>
              )}
            </p>
            <ContactLinksHint />
          </>
        }
      />
    );
  }

  const hasErrors = stage === 'error';
  const isFormDisabled = stage === 'submitting' || stage === 'success';

  const membershipOrNHS = isNHS ? 'NHS' : t('Membership');

  return (
    <>
      {hasErrors && <Alert message={t('EnteredDetailsIncorrect')} type="info" />}
      <p>{t('GuestAccessVerificationWarning', { membershipOrNHS })}</p>
      <Form layout="vertical" disabled={isFormDisabled} {...{ form, onFinish }}>
        <Form.Item
          name="dob"
          validateTrigger="onSubmit"
          getValueFromEvent={(val) => {
            return DateTime.fromFormat(val, 'yyyy-M-d').toFormat('yyyy-MM-dd');
          }}
          rules={dateOfBirthRules}
        >
          <DateInput label={t('DateOfBirth')} alignSubLabel="left" required />
        </Form.Item>

        <Form.Item
          name="membershipNumber"
          label={t('MembershipNumber', { membershipOrNHS })}
          validateTrigger="onBlur"
          rules={[
            { required: true, message: t('ThisFieldIsRequired') },
            isNHS && {
              validator(_, value) {
                if (value && !validate(removeSpaces(value))) {
                  return Promise.reject(Error(t('InvalidNHSNumber')));
                }
                return Promise.resolve();
              },
              message: t('InvalidNHSNumber')
            }
          ].filter(Boolean)}
        >
          <Input css={!isNHS && { textTransform: 'uppercase' }} size="large" />
        </Form.Item>

        <Form.Item>
          <Align x="right">
            <Button htmlType="submit" size="large" type="primary" block loading={isFormDisabled} data-testid="submit">
              {t('Submit')}
            </Button>
          </Align>
        </Form.Item>
      </Form>

      {isNHS && NHSProvider && (
        <>
          <Divider>{t('or')}</Divider>
          <IdentityProviderButton data-testid="nhs-sso-button" identityProvider={{ ...NHSProvider, message: '' }} />
        </>
      )}

      <ContactLinksHint paddingTop />
    </>
  );
};

const RestrictedAccessView: React.FC<{
  isNHS?: boolean;
}> = ({ isNHS }) => {
  return (
    <div className="login-card">
      <LoginTitle title={['Guest', 'Access']} emphasis={1} />
      <RAForm isNHS={isNHS} />
    </div>
  );
};

export default RestrictedAccessView;
